import * as React from 'react'
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  useReactTable,
  TableOptions,
  Table as TableInstance,
  PaginationState,
  ColumnDef,
  Updater,
} from '@tanstack/react-table'
import useTableState from '../useTableState'
import { useCallback, useEffect } from 'react'

type BackendQueryResult<TData> = {
  data?:
    | {
        [key: string]: unknown
      }
    | TData[]
  isLoading: boolean
  isFetching: boolean
  error?: unknown
}

type UseTableWithBackendDataProps<TData, TQueryArg, TResponseData> = Omit<
  TableOptions<TData>,
  | 'data'
  | 'pageCount'
  | 'onPaginationChange'
  | 'onSortingChange'
  | 'onColumnFiltersChange'
  | 'getCoreRowModel'
  | 'getSortedRowModel'
  | 'manualPagination'
  | 'manualSorting'
  | 'manualFiltering'
> & {
  useQuery: (
    arg: TQueryArg,
    options?: Record<string, unknown>,
  ) => BackendQueryResult<TResponseData | TData>
  queryArgsBuilder?: (options: {
    pageIndex: number
    pageSize: number
    sorting: SortingState
    filters: ColumnFiltersState
  }) => TQueryArg
  queryOptions?: Record<string, unknown>
  dataTransformer?: (data: TResponseData | TData[]) => TData[]
  totalCountExtractor?: (data: unknown) => number
  pageSize?: number
  initialSorting?: SortingState
  initialFilters?: ColumnFiltersState
  onDataChange?: (data: TResponseData | TData[]) => void
  onError?: (error: unknown) => void
  columnDefaults?: Partial<ColumnDef<TData, unknown>>
  namespace?: string
  redirectOnEmptyPage?: boolean
}

type UseTableWithBackendDataResult<TData> = {
  table: TableInstance<TData>
  isLoading: boolean
  isFetching: boolean
  error?: unknown
}

const applyColumnDefaults = <TData>(
  columns: ColumnDef<TData, unknown>[],
  defaults: Partial<ColumnDef<TData, unknown>> = { enableResizing: true },
): ColumnDef<TData, unknown>[] => {
  return columns.map((column) => ({
    ...defaults,
    ...column,
  }))
}

function useTableWithBackendData<
  TData extends Record<string, unknown>,
  TQueryArg = Record<string, unknown>,
  TResponseData = unknown,
>({
  columns,
  useQuery,
  queryArgsBuilder,
  queryOptions = {},
  dataTransformer,
  totalCountExtractor = () => 0,
  pageSize = 10,
  initialSorting = [],
  initialFilters = [],
  onError,
  onDataChange,
  columnDefaults = { enableResizing: true },
  namespace,
  redirectOnEmptyPage = true,
  ...tableOptions
}: UseTableWithBackendDataProps<TData, TQueryArg, TResponseData> & {
  columnDefaults?: Partial<ColumnDef<TData, unknown>>
}): UseTableWithBackendDataResult<TData> {
  const tableState = useTableState({
    initialPageSize: pageSize,
    initialSorting,
    initialFilters,
    namespace,
  })

  const columnsWithDefaults = React.useMemo(
    () => applyColumnDefaults(columns, columnDefaults),
    [columns, columnDefaults],
  )

  const handleError = React.useCallback(
    (error: unknown, context: string) => {
      console.error(`Error in ${context}:`, error)
      onError?.(error)
    },
    [onError],
  )

  const queryArgs = React.useMemo(() => {
    if (!queryArgsBuilder) return {} as TQueryArg

    try {
      return queryArgsBuilder({
        pageIndex: tableState.pageIndex,
        pageSize: tableState.pageSize,
        sorting: tableState.sorting,
        filters: tableState.filters,
      })
    } catch (error) {
      handleError(error, 'queryArgsBuilder')
      return {} as TQueryArg
    }
  }, [
    queryArgsBuilder,
    tableState.pageIndex,
    tableState.pageSize,
    tableState.sorting,
    tableState.filters,
    handleError,
  ])

  const { data, isLoading, isFetching, error } = useQuery(
    queryArgs,
    queryOptions,
  )

  const [lastValidData, setLastValidData] = React.useState<
    TResponseData | TData[] | null
  >(null)

  useEffect(() => {
    if (data) {
      onDataChange?.(data as TResponseData | TData[])
      setLastValidData(data as TResponseData | TData[])
    }
  }, [data, onDataChange])

  const transformedData = React.useMemo(() => {
    if (error) return []

    const transformData = (data: TResponseData | TData[]) => {
      if (!dataTransformer) return Array.isArray(data) ? data : [data]
      try {
        return dataTransformer(data)
      } catch (err) {
        handleError(err, 'dataTransformer')
        return []
      }
    }

    if ((isLoading || isFetching) && !data && lastValidData) {
      return transformData(lastValidData)
    }

    if (data) {
      return transformData(data as TResponseData | TData[])
    }

    return []
  }, [
    data,
    dataTransformer,
    handleError,
    isLoading,
    isFetching,
    error,
    lastValidData,
  ])

  useEffect(() => {
    if (data && totalCountExtractor) {
      const totalCount = totalCountExtractor(data)
      const currentData = transformedData

      if (
        totalCount > 0 &&
        currentData.length === 0 &&
        tableState.pageIndex > 0 &&
        redirectOnEmptyPage
      ) {
        tableState.setPageIndex(0)
      }
    }
  }, [data, totalCountExtractor, transformedData, tableState])

  const pageCount = React.useMemo(() => {
    if ((isLoading || isFetching) && !data && lastValidData && !error) {
      try {
        const totalCount = totalCountExtractor(lastValidData)
        return Math.ceil(totalCount / tableState.pageSize)
      } catch (err) {
        handleError(err, 'totalCountExtractor with lastValidData')
        return 0
      }
    }

    if (!data || !totalCountExtractor) return 0
    try {
      const totalCount = totalCountExtractor(data)
      return Math.ceil(totalCount / tableState.pageSize)
    } catch (error) {
      handleError(error, 'totalCountExtractor')
      return 0
    }
  }, [
    data,
    totalCountExtractor,
    tableState.pageSize,
    handleError,
    isLoading,
    isFetching,
    lastValidData,
    error,
  ])

  const handlePaginationChange = useCallback(
    (
      updater: ((state: PaginationState) => PaginationState) | PaginationState,
    ) => {
      if (typeof updater === 'function') {
        const newPagination = updater({
          pageIndex: tableState.pageIndex,
          pageSize: tableState.pageSize,
        })
        tableState.setPageIndex(newPagination.pageIndex)
        if (newPagination.pageSize !== tableState.pageSize) {
          tableState.setPageSize(newPagination.pageSize)
        }
      } else {
        if (updater.pageIndex !== undefined) {
          tableState.setPageIndex(updater.pageIndex)
        }
        if (
          updater.pageSize !== undefined &&
          updater.pageSize !== tableState.pageSize
        ) {
          tableState.setPageSize(updater.pageSize)
        }
      }
    },
    [tableState],
  )

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      if (typeof updater === 'function') {
        const newSorting = updater(tableState.sorting)
        tableState.setSorting(newSorting)
      } else {
        tableState.setSorting(updater)
      }
    },
    [tableState],
  )

  const handleColumnFiltersChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      if (typeof updater === 'function') {
        const newFilters = updater(tableState.filters)
        tableState.setFilters(newFilters)
      } else {
        tableState.setFilters(updater)
      }
    },
    [tableState],
  )

  const table = useReactTable({
    ...tableOptions,
    data: transformedData as TData[],
    columns: columnsWithDefaults,
    pageCount,
    state: {
      ...tableOptions.state,
      sorting: tableState.sorting,
      columnFilters: tableState.filters,
      pagination: {
        pageIndex: tableState.pageIndex,
        pageSize: tableState.pageSize,
      },
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  })

  return {
    table,
    isLoading: isLoading,
    isFetching,
    error,
  }
}

export default useTableWithBackendData
